import BooleanField from '@/components/booleanField';
import JsonField from '@/components/jsonField';
import SelectField from '@/components/selectField';
import TextField from '@/components/textField';
import { useApiContext } from '@/contexts/apiContext';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, FormLabel, Stack, alpha, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  questions: Record<string, any>;
  userResponses: Record<string, any>;
  maxHeight: number;
  onParsedRequirementsChange: (questionId: string, response: any) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
};

const StepDataSource = ({
  questions = {},
  userResponses = {},
  maxHeight,
  onParsedRequirementsChange,
  onBack,
  onCancel,
  onNext,
}: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const theme = useTheme();

  const [containerRef, containerSize] = useElementSize();
  const [actionPanelRef, actionPanelSize] = useElementSize();

  const [dataSources, setDataSources] = useState<any[]>([]);
  const [dataSourceOptions, setDataSourceOptions] = useState<any[]>(
    dataSources.map((d) => ({ value: d.id, label: `${d.name}(${d.dataSourceDef.name})` })),
  );

  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const getDataSources = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getDataSourceInstances(false, false);
        setDataSources(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (userResponses.parsedRequirements.dataSourceType === 'existingDatasource') {
      getDataSources();
    }
  }, [userResponses.parsedRequirements.dataSourceType]);

  const setDataSourceSettings = (settingName: string, value: any) => {
    onParsedRequirementsChange('dataSourceSettings', {
      ...userResponses.parsedRequirements.dataSourceSettings,
      [settingName]: value,
    });
  };

  const setDataSource = (id: string) => {
    const dataSource = dataSources.find((d) => d.id === id);
    onParsedRequirementsChange('existingDataSource', {
      id: id,
      dataSourceDefId: dataSource.entityDefId,
      name: dataSource.name,
    });
  };

  const handleNext = () => {
    const errors: any = {};
    let hasError = false;
    if (userResponses.parsedRequirements.dataSourceType === 'existingDatasource') {
      if (!userResponses.parsedRequirements.existingDataSource) {
        errors.existingDataSource = t('common.errors.requiredField');
        hasError = true;
      }
    }
    if (userResponses.parsedRequirements.dataSourceType === 'azureBlob') {
      if (!userResponses.parsedRequirements.dataSourceSettings?.AZURE_BLOB_ACCOUNT_NAME) {
        errors.AZURE_BLOB_ACCOUNT_NAME = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.AZURE_BLOB_CONTAINER_NAME) {
        errors.AZURE_BLOB_CONTAINER_NAME = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.AZURE_BLOB_ACCOUNT_KEY) {
        errors.AZURE_BLOB_ACCOUNT_KEY = t('common.errors.requiredField');
        hasError = true;
      }
    }

    if (userResponses.parsedRequirements.dataSourceType === 'awsS3') {
      if (!userResponses.parsedRequirements.dataSourceSettings?.S3_BUCKET_NAME_OR_ARN) {
        errors.S3_BUCKET_NAME_OR_ARN = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.S3_REGION) {
        errors.S3_REGION = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.S3_ACCESS_KEY) {
        errors.S3_ACCESS_KEY = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.S3_SECRET_KEY) {
        errors.S3_SECRET_KEY = t('common.errors.requiredField');
        hasError = true;
      }
    }

    if (userResponses.parsedRequirements.dataSourceType === 'googledrive') {
      if (userResponses.parsedRequirements.dataSourceSettings?.GOOGLE_DRIVE_USE_SERVICE_ACCOUNT) {
        if (!userResponses.parsedRequirements.dataSourceSettings?.GOOGLE_DRIVE_SERVICE_ACCOUNT_KEY) {
          errors.GOOGLE_DRIVE_SERVICE_ACCOUNT_KEY = t('common.errors.requiredField');
          hasError = true;
        }
      } else {
        if (!userResponses.parsedRequirements.dataSourceSettings?.GOOGLE_DRIVE_CLIENT_ID) {
          errors.GOOGLE_DRIVE_CLIENT_ID = t('common.errors.requiredField');
          hasError = true;
        }
        if (!userResponses.parsedRequirements.dataSourceSettings?.GOOGLE_DRIVE_CLIENT_SECRET) {
          errors.GOOGLE_DRIVE_CLIENT_SECRET = t('common.errors.requiredField');
          hasError = true;
        }
      }
    }

    if (userResponses.parsedRequirements.dataSourceType === 'mssite') {
      if (!userResponses.parsedRequirements.dataSourceSettings?.DATA_SOURCE_MS_SITE_SITE_ID) {
        errors.DATA_SOURCE_MS_SITE_SITE_ID = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_TENANT_ID) {
        errors.DATA_SOURCE_MS_SITE_AUTH_TENANT_ID = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_CLIENT_ID) {
        errors.DATA_SOURCE_MS_SITE_AUTH_CLIENT_ID = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_CLIENT_SECRET) {
        errors.DATA_SOURCE_MS_SITE_AUTH_CLIENT_SECRET = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_SCOPE) {
        errors.DATA_SOURCE_MS_SITE_AUTH_SCOPE = t('common.errors.requiredField');
        hasError = true;
      }
    }
    if (userResponses.parsedRequirements.dataSourceType === 'sql') {
      if (!userResponses.parsedRequirements.dataSourceSettings?.dbType) {
        errors.dbType = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.host) {
        errors.host = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.dbName) {
        errors.dbName = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.username) {
        errors.username = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.password) {
        errors.password = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.dataSourceSettings?.sqlScript) {
        errors.sqlScript = t('common.errors.requiredField');
        hasError = true;
      }
    }

    setErrors(errors);
    if (!hasError) {
      onNext();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: `${maxHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack alignItems={'flex-start'} spacing={1}>
          <FormLabel required sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
            {'Please enter your data source details'}
          </FormLabel>
          {userResponses.parsedRequirements.dataSourceType === 'azureBlob' && (
            <>
              <TextField
                label={questions.dataSourceSettings.AZURE_BLOB_ACCOUNT_NAME.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.AZURE_BLOB_ACCOUNT_NAME}
                required
                error={errors.AZURE_BLOB_ACCOUNT_NAME}
                onChange={(value) => setDataSourceSettings('AZURE_BLOB_ACCOUNT_NAME', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.AZURE_BLOB_CONTAINER_NAME.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.AZURE_BLOB_CONTAINER_NAME}
                required
                error={errors.AZURE_BLOB_CONTAINER_NAME}
                onChange={(value) => setDataSourceSettings('AZURE_BLOB_CONTAINER_NAME', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.AZURE_BLOB_ACCOUNT_KEY.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.AZURE_BLOB_ACCOUNT_KEY}
                required
                error={errors.AZURE_BLOB_ACCOUNT_KEY}
                onChange={(value) => setDataSourceSettings('AZURE_BLOB_ACCOUNT_KEY', value)}
                type="password"
                fullWidth={false}
              />
            </>
          )}
          {userResponses.parsedRequirements.dataSourceType === 'awsS3' && (
            <>
              <TextField
                label={questions.dataSourceSettings.S3_BUCKET_NAME_OR_ARN.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.S3_BUCKET_NAME_OR_ARN}
                required
                error={errors.S3_BUCKET_NAME_OR_ARN}
                onChange={(value) => setDataSourceSettings('S3_BUCKET_NAME_OR_ARN', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.S3_REGION.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.S3_REGION}
                required
                error={errors.S3_REGION}
                onChange={(value) => setDataSourceSettings('S3_REGION', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.S3_ACCESS_KEY.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.S3_ACCESS_KEY}
                required
                error={errors.S3_ACCESS_KEY}
                onChange={(value) => setDataSourceSettings('S3_ACCESS_KEY', value)}
                type="password"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.S3_SECRET_KEY.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.S3_SECRET_KEY}
                required
                error={errors.S3_SECRET_KEY}
                onChange={(value) => setDataSourceSettings('S3_SECRET_KEY', value)}
                type="password"
                fullWidth={false}
              />
            </>
          )}
          {userResponses.parsedRequirements.dataSourceType === 'googledrive' && (
            <>
              <BooleanField
                label={
                  questions.dataSourceSettings?.GOOGLE_DRIVE_USE_SERVICE_ACCOUNT?.question || 'Use Service Account JSON'
                }
                value={userResponses.parsedRequirements.dataSourceSettings?.GOOGLE_DRIVE_USE_SERVICE_ACCOUNT || false}
                onChange={(value) => setDataSourceSettings('GOOGLE_DRIVE_USE_SERVICE_ACCOUNT', value)}
                error={errors.GOOGLE_DRIVE_USE_SERVICE_ACCOUNT}
              />

              {userResponses.parsedRequirements.dataSourceSettings?.GOOGLE_DRIVE_USE_SERVICE_ACCOUNT ? (
                <JsonField
                  label={
                    questions.dataSourceSettings?.GOOGLE_DRIVE_SERVICE_ACCOUNT_KEY?.question ||
                    'Google account credential json'
                  }
                  value={userResponses.parsedRequirements.dataSourceSettings?.GOOGLE_DRIVE_SERVICE_ACCOUNT_KEY}
                  required
                  error={errors.GOOGLE_DRIVE_SERVICE_ACCOUNT_KEY}
                  onChange={(value) => setDataSourceSettings('GOOGLE_DRIVE_SERVICE_ACCOUNT_KEY', value)}
                />
              ) : (
                <>
                  <TextField
                    label={questions.dataSourceSettings?.GOOGLE_DRIVE_CLIENT_ID?.question || 'Client id'}
                    value={userResponses.parsedRequirements.dataSourceSettings?.GOOGLE_DRIVE_CLIENT_ID}
                    required
                    error={errors.GOOGLE_DRIVE_CLIENT_ID}
                    onChange={(value) => setDataSourceSettings('GOOGLE_DRIVE_CLIENT_ID', value)}
                    type="text"
                    fullWidth={false}
                  />
                  <TextField
                    label={questions.dataSourceSettings?.GOOGLE_DRIVE_CLIENT_SECRET?.question || 'Client secret'}
                    value={userResponses.parsedRequirements.dataSourceSettings?.GOOGLE_DRIVE_CLIENT_SECRET}
                    required
                    error={errors.GOOGLE_DRIVE_CLIENT_SECRET}
                    onChange={(value) => setDataSourceSettings('GOOGLE_DRIVE_CLIENT_SECRET', value)}
                    type="password"
                    fullWidth={false}
                  />
                  <TextField
                    label={questions.dataSourceSettings?.GOOGLE_DRIVE_REFRESH_TOKEN?.question || 'Refresh token'}
                    value={userResponses.parsedRequirements.dataSourceSettings?.GOOGLE_DRIVE_REFRESH_TOKEN}
                    onChange={(value) => setDataSourceSettings('GOOGLE_DRIVE_REFRESH_TOKEN', value)}
                    type="password"
                    fullWidth={false}
                  />
                </>
              )}

              <TextField
                label={questions.dataSourceSettings?.GOOGLE_DRIVE_FOLDER_ID?.question || 'Folder id'}
                value={userResponses.parsedRequirements.dataSourceSettings?.GOOGLE_DRIVE_FOLDER_ID}
                onChange={(value) => setDataSourceSettings('GOOGLE_DRIVE_FOLDER_ID', value)}
                type="text"
                fullWidth={false}
              />
            </>
          )}
          {userResponses.parsedRequirements.dataSourceType === 'mssite' && (
            <>
              <TextField
                label={questions.dataSourceSettings.DATA_SOURCE_MS_SITE_SITE_ID.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.DATA_SOURCE_MS_SITE_SITE_ID}
                required
                error={errors.DATA_SOURCE_MS_SITE_SITE_ID}
                onChange={(value) => setDataSourceSettings('DATA_SOURCE_MS_SITE_SITE_ID', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.DATA_SOURCE_MS_SITE_BASE_PATH.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.DATA_SOURCE_MS_SITE_BASE_PATH}
                error={errors.DATA_SOURCE_MS_SITE_BASE_PATH}
                onChange={(value) => setDataSourceSettings('DATA_SOURCE_MS_SITE_BASE_PATH', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.DATA_SOURCE_MS_SITE_AUTH_TENANT_ID.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_TENANT_ID}
                required
                error={errors.DATA_SOURCE_MS_SITE_AUTH_TENANT_ID}
                onChange={(value) => setDataSourceSettings('DATA_SOURCE_MS_SITE_AUTH_TENANT_ID', value)}
                type="text"
                fullWidth={false}
              />

              <TextField
                label={questions.dataSourceSettings.DATA_SOURCE_MS_SITE_AUTH_CLIENT_ID.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_CLIENT_ID}
                required
                error={errors.DATA_SOURCE_MS_SITE_AUTH_CLIENT_ID}
                onChange={(value) => setDataSourceSettings('DATA_SOURCE_MS_SITE_AUTH_CLIENT_ID', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.DATA_SOURCE_MS_SITE_AUTH_CLIENT_SECRET.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_CLIENT_SECRET}
                required
                error={errors.DATA_SOURCE_MS_SITE_AUTH_CLIENT_SECRET}
                onChange={(value) => setDataSourceSettings('DATA_SOURCE_MS_SITE_AUTH_CLIENT_SECRET', value)}
                type="password"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.DATA_SOURCE_MS_SITE_AUTH_SCOPE.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_SCOPE}
                required
                error={errors.DATA_SOURCE_MS_SITE_AUTH_SCOPE}
                onChange={(value) => setDataSourceSettings('DATA_SOURCE_MS_SITE_AUTH_SCOPE', value)}
                type="text"
                fullWidth={false}
              />
            </>
          )}
          {userResponses.parsedRequirements.dataSourceType === 'sql' && (
            <>
              <SelectField
                label={questions.dataSourceSettings.dbType.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.dbType || 'postgresql'}
                options={questions.dataSourceSettings.dbType.options}
                required
                error={errors.dbType}
                onChange={(value) => setDataSourceSettings('dbType', value)}
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.host.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.host}
                required
                error={errors.host}
                onChange={(value) => setDataSourceSettings('host', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.port.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.port}
                error={errors.port}
                onChange={(value) => setDataSourceSettings('port', value)}
                type="number"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.dbName.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.dbName}
                required
                error={errors.dbName}
                onChange={(value) => setDataSourceSettings('dbName', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.username.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.username}
                required
                error={errors.username}
                onChange={(value) => setDataSourceSettings('username', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.password.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.password}
                required
                error={errors.password}
                onChange={(value) => setDataSourceSettings('password', value)}
                type="password"
                fullWidth={false}
              />
              <TextField
                label={questions.dataSourceSettings.sqlScript.question}
                value={userResponses.parsedRequirements.dataSourceSettings?.sqlScript}
                required
                error={errors.sqlScript}
                onChange={(value) => setDataSourceSettings('sqlScript', value)}
                type="text"
                fullWidth={false}
                multiline
                rows={4}
              />
            </>
          )}

          {userResponses.parsedRequirements.dataSourceType === 'existingDatasource' && (
            <SelectField
              label={questions.dataSourceSettings.existingDataSource.question}
              value={userResponses.parsedRequirements.existingDataSource?.id || ''}
              options={dataSourceOptions}
              required
              error={errors.existingDataSource}
              onChange={setDataSource}
              fullWidth={false}
            />
          )}
        </Stack>
      </Box>
      <Box
        ref={actionPanelRef}
        sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            {t('common.buttons.back')}
          </Button>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="text"
              onClick={handleCancel}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              disabled={!userResponses.parsedRequirements.dataSourceType}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 2.5,
                color: 'text.primary',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {t('common.buttons.next')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default StepDataSource;
